import { Check, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

const api = async (path, options = {}) => {
  const response = await fetch(path, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {})
    },
    ...options,
    body: options.body ? JSON.stringify(options.body) : undefined
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Request failed");
  }
  return data;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const round1 = (value) => Math.round(value * 10) / 10;

const formatSeconds = (value) => {
  const rounded = round1(value);
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}s`;
};

const MODES = {
  shock: { label: "Shock", maxKey: "maxShockIntensity" },
  vibrate: { label: "Vibrate", maxKey: "maxVibrateIntensity" }
};

const DEFAULT_VALUES = {
  shock: { intensity: 10, duration: 0.1 },
  vibrate: { intensity: 35, duration: 0.3 }
};

function App() {
  const [status, setStatus] = useState(null);
  const [bootError, setBootError] = useState("");

  const loadStatus = useCallback(async () => {
    const next = await api("/api/status");
    setStatus(next);
    setBootError("");
    return next;
  }, []);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      loadStatus().catch((err) => setBootError(err.message));
    };
    refresh();
    const id = window.setInterval(refresh, 4000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadStatus]);

  if (!status) {
    return (
      <div className="boot">
        {bootError && (
          <>
            <p>{bootError}</p>
            <button className="send" onClick={() => loadStatus().catch((err) => setBootError(err.message))}>
              Retry
            </button>
          </>
        )}
      </div>
    );
  }

  if (!status.authenticated) {
    return <LoginView onLogin={setStatus} />;
  }

  return <Console status={status} setStatus={setStatus} loadStatus={loadStatus} />;
}

function LoginView({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      onLogin(await api("/api/session", { method: "POST", body: { password } }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="login" onSubmit={submit}>
      <input
        autoFocus
        name="password"
        type="password"
        autoComplete="current-password"
        placeholder="Password"
        aria-label="Password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />
      {error && <p className="note error">{error}</p>}
      <button className="send" disabled={busy || !password}>
        {busy ? <Loader2 size={22} className="spin" /> : "Unlock"}
      </button>
    </form>
  );
}

function Console({ status, setStatus, loadStatus }) {
  const [mode, setMode] = useState("shock");
  const [values, setValues] = useState(DEFAULT_VALUES);
  const [phase, setPhase] = useState("idle");
  const [toast, setToast] = useState("");
  const [linking, setLinking] = useState(false);
  const sentTimer = useRef(null);

  const { limits } = status;
  const ready = status.configured || status.dryRun;
  const current = values[mode];
  const maxIntensity = limits[MODES[mode].maxKey];
  const minDuration = limits.minDurationSeconds;
  const maxDuration = limits.maxDurationSeconds;

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(""), 4000);
    return () => window.clearTimeout(id);
  }, [toast]);

  useEffect(() => () => window.clearTimeout(sentTimer.current), []);

  const adjust = (key, fn) =>
    setValues((prev) => ({
      ...prev,
      [mode]: { ...prev[mode], [key]: fn(prev[mode][key]) }
    }));

  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    if (phase === "sent") setPhase("idle");
  };

  const send = async () => {
    if (phase === "sending") return;
    window.clearTimeout(sentTimer.current);
    setPhase("sending");
    setToast("");
    try {
      const response = await api("/api/operate", {
        method: "POST",
        body: { operation: mode, intensity: current.intensity, duration: current.duration }
      });
      if (response.status) setStatus(response.status);
      navigator.vibrate?.(12);
      setPhase("sent");
      sentTimer.current = window.setTimeout(() => setPhase("idle"), 1400);
    } catch (err) {
      setPhase("idle");
      setToast(err.message);
      loadStatus().catch(() => {});
    }
  };

  const linkPiShock = () => {
    setToast("");
    setLinking(true);

    const channel = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    const loginWindow = window.open("about:blank", "_blank", "width=520,height=760");
    const socket = new WebSocket(`wss://relay.pishock.com/${channel}`);
    let settled = false;

    const finish = () => {
      settled = true;
      window.clearTimeout(timer);
      setLinking(false);
      socket.close();
    };

    const timer = window.setTimeout(() => {
      if (!settled) {
        finish();
        setToast("PiShock link timed out");
      }
    }, 90_000);

    socket.onmessage = async (event) => {
      const message = String(event.data || "");
      if (message.toLowerCase() === "connected.") {
        const url = `https://login.pishock.com?proto=socket&channel=${channel}`;
        if (loginWindow && !loginWindow.closed) {
          loginWindow.location.href = url;
        } else {
          window.open(url, "_blank", "width=520,height=760");
        }
        return;
      }

      try {
        const payload = JSON.parse(message);
        if (payload.UserId && payload.Token) {
          const next = await api("/api/pishock-token", {
            method: "POST",
            body: { userId: payload.UserId, token: payload.Token }
          });
          finish();
          setStatus(next);
          if (loginWindow && !loginWindow.closed) loginWindow.close();
        }
      } catch (err) {
        finish();
        setToast(err.message);
      }
    };

    socket.onerror = () => {
      finish();
      setToast("PiShock link failed");
    };
  };

  const durationSpan = Math.log(maxDuration / minDuration) || 1;

  return (
    <main className="app">
      {toast && (
        <div className="toast" role="alert" onClick={() => setToast("")}>
          {toast}
        </div>
      )}

      <div className="segmented" role="tablist" aria-label="Mode">
        {Object.entries(MODES).map(([key, item]) => (
          <button
            key={key}
            role="tab"
            aria-selected={mode === key}
            className={mode === key ? "on" : ""}
            onClick={() => switchMode(key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="controls">
        <Control
          label="Intensity"
          value={current.intensity}
          display={`${current.intensity}%`}
          min={1}
          max={maxIntensity}
          toPos={(value) => (value - 1) / Math.max(1, maxIntensity - 1)}
          fromPos={(pos) => Math.round(1 + pos * (maxIntensity - 1))}
          stepUp={(value) => value + 1}
          stepDown={(value) => value - 1}
          adjust={(fn) => adjust("intensity", (value) => clamp(fn(value), 1, maxIntensity))}
        />
        <Control
          label="Duration"
          value={current.duration}
          display={formatSeconds(current.duration)}
          min={minDuration}
          max={maxDuration}
          toPos={(value) => Math.log(value / minDuration) / durationSpan}
          fromPos={(pos) => round1(minDuration * Math.exp(pos * durationSpan))}
          stepUp={(value) => round1(value < 1 ? value + 0.1 : value + 0.5)}
          stepDown={(value) => round1(value <= 1 ? value - 0.1 : Math.max(1, value - 0.5))}
          adjust={(fn) => adjust("duration", (value) => clamp(fn(value), minDuration, maxDuration))}
        />
      </div>

      <div className="dock">
        {status.dryRun && <p className="note">Dry run · nothing is sent</p>}
        {!ready && status.setupError && <p className="note">{status.setupError}</p>}

        {ready ? (
          <button className={`send ${phase}`} disabled={phase === "sending"} onClick={send}>
            {phase === "sending" && <Loader2 size={24} className="spin" />}
            {phase === "sent" && (
              <>
                <Check size={24} strokeWidth={2.5} />
                Sent
              </>
            )}
            {phase === "idle" && `Send ${MODES[mode].label.toLowerCase()}`}
          </button>
        ) : (
          <button className="send" disabled={linking} onClick={linkPiShock}>
            {linking ? <Loader2 size={24} className="spin" /> : status.tokenLinked ? "Relink PiShock" : "Link PiShock"}
          </button>
        )}
      </div>
    </main>
  );
}

function Control({ label, value, display, min, max, toPos, fromPos, stepUp, stepDown, adjust }) {
  const barRef = useRef(null);
  const dragging = useRef(false);
  const knob = 44;

  const setFromPointer = (clientX) => {
    const rect = barRef.current.getBoundingClientRect();
    const pos = clamp((clientX - rect.left - knob / 2) / (rect.width - knob), 0, 1);
    adjust(() => fromPos(pos));
  };

  const onKeyDown = (event) => {
    const moves = {
      ArrowRight: stepUp,
      ArrowUp: stepUp,
      ArrowLeft: stepDown,
      ArrowDown: stepDown,
      Home: () => min,
      End: () => max
    };
    if (!moves[event.key]) return;
    event.preventDefault();
    adjust(moves[event.key]);
  };

  const pos = clamp(toPos(value), 0, 1);

  return (
    <section className="control">
      <div className="control-head">
        <span>{label}</span>
        <output>{display}</output>
      </div>
      <div
        ref={barRef}
        className="bar"
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={display}
        onKeyDown={onKeyDown}
        onPointerDown={(event) => {
          dragging.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
          setFromPointer(event.clientX);
        }}
        onPointerMove={(event) => {
          if (dragging.current) setFromPointer(event.clientX);
        }}
        onPointerUp={() => {
          dragging.current = false;
        }}
        onPointerCancel={() => {
          dragging.current = false;
        }}
      >
        <div className="bar-fill" style={{ width: `calc(${knob}px + (100% - ${knob}px) * ${pos})` }} />
      </div>
    </section>
  );
}

export default App;
